import type { Icon } from "@phosphor-icons/react";
import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export type EnrollmentProgressStep = {
  id: number;
  label: string;
  heading: string;
  icon: Icon;
};

type EnrollmentStepProgressProps = {
  steps: readonly EnrollmentProgressStep[];
  currentStepIndex: number;
  onSelectStep: (index: number) => void;
};

export function EnrollmentStepProgress({
  steps,
  currentStepIndex,
  onSelectStep,
}: EnrollmentStepProgressProps) {
  return (
    <nav aria-label="Koraci prijave" className="mb-7 lg:mb-10">
      <ol
        className="flex gap-3 overflow-x-auto pb-1 lg:grid lg:overflow-visible lg:pb-0"
        style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
      >
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStepIndex === index;
          const isDone = currentStepIndex > index;
          const canSelect = isDone;

          return (
            <li key={step.id} className="min-w-[8.5rem] lg:min-w-0">
              <button
                type="button"
                onClick={() => canSelect && onSelectStep(index)}
                disabled={!canSelect && !isActive}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "group w-full text-left transition-opacity",
                  canSelect ? "cursor-pointer" : "cursor-default",
                  !isDone && !isActive && "opacity-45"
                )}
              >
                <span
                  className={cn(
                    "block h-1.5 rounded-full transition-colors",
                    isDone
                      ? "bg-emerald-500"
                      : isActive
                        ? "bg-foreground"
                        : "bg-border"
                  )}
                />
                <span className="mt-2.5 flex min-h-12 items-start gap-2.5">
                  <span
                    className={cn(
                      "flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
                      isDone
                        ? "bg-emerald-100 text-emerald-700"
                        : isActive
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                    )}
                  >
                    {isDone ? (
                      <Check className="size-4" />
                    ) : (
                      <Icon className="size-4" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        "block text-[11px] font-semibold uppercase tracking-[0.14em]",
                        isActive
                          ? "text-foreground"
                          : isDone
                            ? "text-emerald-700"
                            : "text-muted-foreground"
                      )}
                    >
                      Korak {index + 1}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 block text-sm font-semibold leading-snug",
                        isActive
                          ? "text-foreground"
                          : isDone
                            ? "text-foreground/80"
                            : "text-muted-foreground"
                      )}
                    >
                      <span className="sm:hidden">{step.label}</span>
                      <span className="hidden sm:inline">{step.heading}</span>
                    </span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
